'use client';

import React, { useState } from 'react';
import { Layers, Plus, Trash2, Save, History, ArrowDownRight, ArrowUpRight, RefreshCcw } from 'lucide-react';
import { colors, baseStyles, formatNum } from './SharedUI';

const formatUSD = (val) => {
  if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(0)}`;
};

export default function LiquidationHeatmapAnalyzer({ 
  symbol = 'BTCUSDT', 
  smartMoneyRef, 
  liveDataRef 
}) {
  // Hàm trích xuất và quy đổi dữ liệu chính xác từ Refs theo yêu cầu
  const extractDataFromRefs = () => {
    const live = liveDataRef?.current || {};
    const smart = smartMoneyRef?.current || {};

    const price = Number(live.last) || 60000;
    const oi = Number(live.oiContract) || 10000;
    
    // Xử lý longShortRatio: Nếu là hệ số dạng tỷ lệ (VD: 1.5) thì quy đổi ra %, nếu đã là phần trăm thì giữ nguyên
    const rawRatio = Number(smart.longShortRatio);
    let lRatio = 50;
    if (!isNaN(rawRatio) && rawRatio > 0) {
      lRatio = rawRatio > 10 ? rawRatio : (rawRatio / (1 + rawRatio)) * 100;
    }

    return { 
      price, 
      oi, 
      lRatio: Math.min(95, Math.max(5, Math.round(lRatio))) 
    };
  };

  // Khởi tạo state ban đầu từ Refs
  const initialData = extractDataFromRefs();

  const [editablePrice, setEditablePrice] = useState(initialData.price);
  const [editableOI, setEditableOI] = useState(initialData.oi);
  const [editableLongRatio, setEditableLongRatio] = useState(initialData.lRatio);

  // Phân bổ đòn bẩy động (Có thể thêm, sửa, xóa)
  const [leverageTiers, setLeverageTiers] = useState([
    { id: 1, leverage: 10, percentage: 20 },
    { id: 2, leverage: 20, percentage: 50 },
    { id: 3, leverage: 50, percentage: 30 },
  ]);

  // Kết quả phân tích hiện tại và Lịch sử lưu trữ
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [history, setHistory] = useState([]);

  // Hàm đồng bộ dữ liệu mới nhất từ Refs khi người dùng bấm nút làm mới
  const handleSyncFromRefs = () => {
    const latest = extractDataFromRefs();
    setEditablePrice(latest.price);
    setEditableOI(latest.oi);
    setEditableLongRatio(latest.lRatio);
  };

  // Xử lý thêm tầng đòn bẩy mới
  const handleAddTier = () => {
    const newId = leverageTiers.length > 0 ? Math.max(...leverageTiers.map(t => t.id)) + 1 : 1;
    setLeverageTiers([...leverageTiers, { id: newId, leverage: 100, percentage: 0 }]);
  };

  // Xử lý xóa tầng đòn bẩy
  const handleRemoveTier = (id) => {
    setLeverageTiers(leverageTiers.filter(t => t.id !== id));
  };

  // Cập nhật giá trị tầng đòn bẩy
  const handleTierChange = (id, field, value) => {
    setLeverageTiers(leverageTiers.map(t => t.id === id ? { ...t, [field]: parseFloat(value) || 0 } : t));
  };

  // Tổng tỷ trọng % phân bổ đòn bẩy
  const totalPercentage = leverageTiers.reduce((sum, t) => sum + t.percentage, 0);

  // ⚡ THỰC THI THUẬT TOÁN MÔ PHỎNG LIQUIDATION HEATMAP
  const handleAnalyze = () => {
    // Tự động sync dữ liệu mới nhất từ Refs trước khi tính toán
    const currentRefData = extractDataFromRefs();
    const price = parseFloat(editablePrice || currentRefData.price) || 0;
    const oi = parseFloat(editableOI || currentRefData.oi) || 0;
    const lRatio = parseFloat(editableLongRatio || currentRefData.lRatio) || 50;
    const sRatio = 100 - lRatio;

    if (price <= 0 || oi <= 0) {
      alert('Giá (last) và Open Interest (oiContract) phải lớn hơn 0!');
      return;
    }

    const totalLongCoins = oi * (lRatio / 100);
    const totalShortCoins = oi * (sRatio / 100);

    let clusters = [];

    // Tính toán cho phe Long (Giá thanh lý nằm DƯỚI giá hiện tại)
    leverageTiers.forEach(tier => {
      if (tier.percentage <= 0 || tier.leverage <= 1) return;
      const coinsAtRisk = totalLongCoins * (tier.percentage / 100);
      const usdValueAtRisk = coinsAtRisk * price;
      
      const liqPrice = price * (1 - 1 / tier.leverage);

      clusters.push({
        side: 'LONG',
        leverage: tier.leverage,
        percentage: tier.percentage,
        coins: coinsAtRisk,
        usdValue: usdValueAtRisk,
        liqPrice: parseFloat(liqPrice.toFixed(2)),
        distancePct: parseFloat((((liqPrice - price) / price) * 100).toFixed(2))
      });
    });

    // Tính toán cho phe Short (Giá thanh lý nằm TRÊN giá hiện tại)
    leverageTiers.forEach(tier => {
      if (tier.percentage <= 0 || tier.leverage <= 1) return;
      const coinsAtRisk = totalShortCoins * (tier.percentage / 100);
      const usdValueAtRisk = coinsAtRisk * price;
      
      const liqPrice = price * (1 + 1 / tier.leverage);

      clusters.push({
        side: 'SHORT',
        leverage: tier.leverage,
        percentage: tier.percentage,
        coins: coinsAtRisk,
        usdValue: usdValueAtRisk,
        liqPrice: parseFloat(liqPrice.toFixed(2)),
        distancePct: parseFloat((((liqPrice - price) / price) * 100).toFixed(2))
      });
    });

    // Sắp xếp các cụm thanh lý theo giá tăng dần
    clusters.sort((a, b) => a.liqPrice - b.liqPrice);

    const analysisResult = {
      id: Date.now(),
      timestamp: new Date().toLocaleTimeString() + ' ' + new Date().toLocaleDateString(),
      symbol: symbol.toUpperCase(),
      currentPrice: price,
      openInterest: oi,
      totalValueUSD: oi * price,
      longRatio: lRatio,
      shortRatio: sRatio,
      clusters
    };

    setCurrentAnalysis(analysisResult);
  };

  // Lưu kết quả vào lịch sử
  const handleSaveToHistory = () => {
    if (!currentAnalysis) return;
    if (history.some(h => h.id === currentAnalysis.id)) {
      alert('Kết quả này đã có trong lịch sử!');
      return;
    }
    setHistory([currentAnalysis, ...history]);
    alert('Đã lưu vào lịch sử phân tích thành công!');
  };

  return (
    <div style={{ backgroundColor: '#0b0e14', color: '#ffffff', minHeight: '100vh', padding: '20px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* HEADER TITLE */}
        <div style={{ ...baseStyles.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '1px solid #334155' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', color: '#ffffff', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Layers size={24} color="#3b82f6" /> ESTIMATED LIQUIDATION HEATMAP SIMULATOR ({symbol.toUpperCase()})
            </h1>
            <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#94a3b8' }}>
              Đọc dữ liệu realtime từ <code style={{ color: '#38bdf8' }}>liveDataRef.current.last</code>, <code style={{ color: '#38bdf8' }}>oiContract</code> và <code style={{ color: '#38bdf8' }}>smartMoneyRef.current.longShortRatio</code>.
            </p>
          </div>
          
          <button 
            onClick={handleSyncFromRefs}
            style={{ backgroundColor: '#334155', color: '#ffffff', border: '1px solid #475569', padding: '8px 12px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
            title="Đồng bộ dữ liệu mới nhất từ Refs"
          >
            <RefreshCcw size={14} /> Sync từ Refs
          </button>
        </div>

        {/* KHU VỰC THÔNG SỐ ĐẦU VÀO & CẤU HÌNH ĐÒN BẨY */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          
          {/* CỘT TRÁI: THÔNG SỐ LẤY TỪ REFS */}
          <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ borderBottom: '1px solid #334155', paddingBottom: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#fbbf24' }}>
                ⚙️ 1. THÔNG SỐ ĐẦU VÀO (TỪ REFS)
              </strong>
            </div>

            <div>
              <label style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>MÃ TOKEN / SYMBOL</label>
              <input 
                type="text" 
                value={symbol} 
                disabled 
                style={{ ...baseStyles.input, width: '100%', color: '#94a3b8', fontWeight: 'bold', marginTop: '4px', backgroundColor: 'rgba(255,255,255,0.05)' }} 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>GIÁ (last)</label>
                <input 
                  type="number" 
                  value={editablePrice} 
                  onChange={(e) => setEditablePrice(e.target.value)} 
                  style={{ ...baseStyles.input, width: '100%', color: '#facc15', fontWeight: 'bold', marginTop: '4px' }} 
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 'bold' }}>OI (oiContract)</label>
                <input 
                  type="number" 
                  value={editableOI} 
                  onChange={(e) => setEditableOI(e.target.value)} 
                  style={{ ...baseStyles.input, width: '100%', color: '#38bdf8', fontWeight: 'bold', marginTop: '4px' }} 
                />
              </div>
            </div>
            <div style={{ fontSize: '10px', color: '#64748b' }}>Tổng giá trị danh nghĩa OI: {formatUSD(editableOI * editablePrice)}</div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold', color: '#94a3b8' }}>
                <span>TỶ LỆ LONG / SHORT (longShortRatio)</span>
                <span style={{ color: '#22c55e' }}>Long: {editableLongRatio}%</span> | <span style={{ color: '#ef4444' }}>Short: {100 - editableLongRatio}%</span>
              </div>
              <input 
                type="range" 
                min="10" 
                max="90" 
                value={editableLongRatio} 
                onChange={(e) => setEditableLongRatio(parseInt(e.target.value))} 
                style={{ width: '100%', accentColor: '#3b82f6', marginTop: '8px', cursor: 'pointer' }} 
              />
            </div>
          </div>

          {/* CỘT PHẢI: CẤU HÌNH PHÂN BỔ ĐÒN BẨY ĐỘNG */}
          <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', paddingBottom: '8px' }}>
              <strong style={{ fontSize: '14px', color: '#65a30d' }}>
                📊 2. MÔ HÌNH PHÂN BỔ ĐÒN BẨY
              </strong>
              <button 
                onClick={handleAddTier}
                style={{ backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Plus size={12} /> Thêm tầng
              </button>
            </div>

            <div style={{ fontSize: '11px', color: totalPercentage !== 100 ? '#f59e0b' : '#22c55e', fontWeight: 'bold' }}>
              Tổng tỷ trọng phân bổ: {totalPercentage}% {totalPercentage !== 100 && '(Khuyến nghị tổng bằng 100%)'}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
              {leverageTiers.map((tier) => (
                <div key={tier.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.3)', padding: '6px', borderRadius: '6px', border: '1px solid #334155' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>Đòn bẩy:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input 
                        type="number" 
                        value={tier.leverage} 
                        onChange={(e) => handleTierChange(tier.id, 'leverage', e.target.value)} 
                        style={{ ...baseStyles.input, width: '100%', padding: '4px', fontSize: '12px', color: '#fff' }} 
                      />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>x</span>
                    </div>
                  </div>

                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>Tỷ trọng (%):</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <input 
                        type="number" 
                        value={tier.percentage} 
                        onChange={(e) => handleTierChange(tier.id, 'percentage', e.target.value)} 
                        style={{ ...baseStyles.input, width: '100%', padding: '4px', fontSize: '12px', color: '#fff' }} 
                      />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>%</span>
                    </div>
                  </div>

                  <button 
                    onClick={() => handleRemoveTier(tier.id)}
                    style={{ backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '4px', marginTop: '14px' }}
                    title="Xóa tầng này"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <button 
              onClick={handleAnalyze}
              style={{ backgroundColor: '#16a34a', color: '#ffffff', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: '900', fontSize: '14px', cursor: 'pointer', marginTop: 'auto', boxShadow: '0 0 12px rgba(22, 163, 74, 0.4)' }}
            >
              🚀 CHẠY MÔ PHỎNG PHÂN TÍCH HEATMAP
            </button>
          </div>

        </div>

        {/* KẾT QUẢ PHÂN TÍCH HIỆN TẠI */}
        {currentAnalysis && (
          <div style={{ ...baseStyles.card, border: '1px solid #3b82f6', background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', paddingBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <strong style={{ fontSize: '16px', color: '#60a5fa' }}>
                  🎯 KẾT QUẢ MÔ PHỎNG THANH LÝ: {currentAnalysis.symbol} (Giá cơ sở: ${currentAnalysis.currentPrice.toLocaleString()})
                </strong>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  Thời gian phân tích: {currentAnalysis.timestamp} | Tổng OI: {currentAnalysis.openInterest.toLocaleString()} Coin ({formatUSD(currentAnalysis.totalValueUSD)})
                </div>
              </div>

              <button 
                onClick={handleSaveToHistory}
                style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none', padding: '8px 14px', borderRadius: '6px', fontWeight: 'bold', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Save size={14} /> Lưu vào lịch sử so sánh
              </button>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #334155', color: '#94a3b8' }}>
                    <th style={{ padding: '8px' }}>VỊ THẾ (SIDE)</th>
                    <th style={{ padding: '8px' }}>ĐÒN BẨY</th>
                    <th style={{ padding: '8px' }}>TỶ TRỌNG</th>
                    <th style={{ padding: '8px' }}>GIÁ THANH LÝ (EST. LIQ PRICE)</th>
                    <th style={{ padding: '8px' }}>KHOẢNG CÁCH (%)</th>
                    <th style={{ padding: '8px' }}>KHỐI LƯỢNG RỦI RO (COINS)</th>
                    <th style={{ padding: '8px' }}>GIÁ TRỊ DANH NGHĨA (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAnalysis.clusters.map((cluster, index) => {
                    const isLong = cluster.side === 'LONG';
                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #1e293b', backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: isLong ? '#22c55e' : '#ef4444', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          {isLong ? <ArrowDownRight size={14} /> : <ArrowUpRight size={14} />} {cluster.side}
                        </td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: '#fff' }}>{cluster.leverage}x</td>
                        <td style={{ padding: '10px', color: '#cbd5e1' }}>{cluster.percentage}%</td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: '#facc15', fontFamily: 'monospace' }}>
                          ${cluster.liqPrice.toLocaleString()}
                        </td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: cluster.distancePct < 0 ? '#ef4444' : '#22c55e' }}>
                          {cluster.distancePct > 0 ? `+${cluster.distancePct}%` : `${cluster.distancePct}%`}
                        </td>
                        <td style={{ padding: '10px', color: '#cbd5e1' }}>{cluster.coins.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td style={{ padding: '10px', fontWeight: 'bold', color: '#38bdf8' }}>{formatUSD(cluster.usdValue)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LỊCH SỬ LƯU TRỮ VÀ SO SÁNH */}
        {history.length > 0 && (
          <div style={{ ...baseStyles.card, display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <strong style={{ fontSize: '14px', color: '#a3e635', borderBottom: '1px solid #334155', paddingBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <History size={16} /> LỊCH SỬ CÁC LẦN PHÂN TÍCH ĐÃ LƯU ({history.length})
            </strong>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '12px' }}>
              {history.map((item) => (
                <div key={item.id} style={{ backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid #334155', borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#ffffff' }}>{item.symbol}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>{item.timestamp}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#cbd5e1', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Giá: ${item.currentPrice.toLocaleString()}</span>
                    <span>OI: {item.openInterest.toLocaleString()}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: '#cbd5e1', display: 'flex', justifyContent: 'space-between' }}>
                    <span>Long/Short: {item.longRatio}% / {item.shortRatio}%</span>
                    <span style={{ color: '#38bdf8' }}>Tổng giá trị: {formatUSD(item.totalValueUSD)}</span>
                  </div>
                  <div style={{ fontSize: '10px', color: '#f59e0b', fontStyle: 'italic', borderTop: '1px dashed #334155', paddingTop: '6px', marginTop: '2px' }}>
                    Đã lưu {item.clusters.length} cụm mức giá thanh lý mục tiêu.
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
